// Copyright (c) Docugami, Inc. All rights reserved.

import { IconButton, Icon } from "@fluentui/react";
import React, { useState } from 'react';
import 'components/settings/css/SettingsPanel.scss'
import styled from 'styled-components';
import { TelemetryContext } from 'telemetry/TelemetryContext';
import { SmallButton, SmallButtonLabel } from 'components/common/Fonts';
import { RequestState, useAsyncHelper } from 'hooks/AsyncHelper';
import { useTimeout } from 'hooks/Timeout';

const ContextContainer = styled.div`
    display: flex;
    justify-content:center;
    flex-direction: column;
    margin-top: auto;
`

const ContextHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
`

const ContentHeader = styled(SmallButtonLabel)`
    font-weight: 800;
`

const ContentContainer = styled.div<{ indent: number }>`
    margin-left: ${props => props.indent}px;
    display: flex;
    flex-direction: column;
`

const ContentText = styled(SmallButtonLabel)`
    white-space: pre;
`

const ContextItemContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const ContentScrollContainer = styled.div`
    overflow-x: auto;
    overflow-y: auto;
`

const ContextHeaderButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    cursor: pointer;
    align-items: center;
`

export const TelemetryContextPanel = (): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [showCopied, setShowCopied] = useState(false);

    const { execute, state } = useAsyncHelper("Copy to clipboard");
    useTimeout(() => setShowCopied(false), showCopied ? 1000 : undefined);

    function onCopy() {
        if (state !== RequestState.InProgress) {
            setShowCopied(true);
            execute(navigator.clipboard.writeText(JSON.stringify(TelemetryContext.context, null, 2)));
        }
    }

    function getEntries(item: any, depth: number, indent: number): JSX.Element[] {
        return Object.entries(item).flatMap(entry => {
            const [key, value] = entry;

            function getContent() {
                const valueType = typeof value;
                if (valueType === 'object') {
                    return getEntries(value, depth + 1, indent);
                }
                else if (valueType === 'string') {
                    return <ContentText>{value as string}</ContentText>;
                }
                else {
                    return <ContentText>{JSON.stringify(value, null, indent)}</ContentText>;
                }
            }

            return (
                <ContextItemContainer key={key}>
                    <ContentContainer indent={(depth) * indent}>
                        <div>
                            <ContentHeader>{key}</ContentHeader>
                        </div>
                        {getContent()}
                    </ContentContainer>
                </ContextItemContainer>
            );
        });
    }

    return (
        <div className="settingsCollection">
            <ContextContainer>
                <ContextHeader>
                    <ContextHeaderButtonContainer onClick={() => setIsOpen(x => !x)}>
                        <Icon iconName={isOpen ? "ChevronDown" : "ChevronRight"} />
                        <SmallButton>Session Info</SmallButton>
                    </ContextHeaderButtonContainer>
                    <IconButton iconProps={{ iconName: "Copy" }} onClick={() => onCopy()} />
                    {showCopied && <SmallButtonLabel>Copied!</SmallButtonLabel>}
                </ContextHeader>
                <ContentScrollContainer>
                    {isOpen && getEntries(TelemetryContext.context, 0, 10)}
                </ContentScrollContainer>
            </ContextContainer>
        </div>
    );
}
